import { Grade, Locale } from "@/enums"; // Assuming Grade and Locale enums are available
import curriculumGraphqlRequest from "../curriculumGraphqlRequest";
import {
  GQL_CLONE_CURRICULUM_LESSON_PLANS,
  GQL_CURRICULUM_LESSON_PLANS_DATA,
  GQL_GET_CURRICULUM_LESSON_PLANS_AVAILABLE,
  GQL_SAVE_CURRICULUM_LESSON_PLAN,
} from "./curriculumLessonPlansQuery";

// Define Status, SkillType, Window enums if not already defined/imported
// enum Status { ... }
// enum SkillType { ... }
// enum Window { ... }

export enum Status {
	ACTIVE = "ACTIVE",
	INACTIVE = "INACTIVE",
}

// --- GraphQL Input Schema Types ---

export type LessonSkillInput = {
	id: string;
	skillId: string;
	locale?: Locale;
	// skillType?: SkillType;
	skillName?: string;
	displayName?: string;
	sequence?: number;
	// grade?: Grade;
	windowPeriod?: Window;
	status?: Status;
};

export type CurriculumLessonInput = {
	id: string;
	// lessonName?: string;
	displayName?: string;
	duration?: number;
	durationType?: string;
	// lessonDescription?: string;
	sequence?: number;
	dateFrom?: string | null;
	dateTo?: string | null;
	status?: Status;
	skills?: LessonSkillInput[];
};

export type CurriculumUnitInput = {
	id: string;
	// unitName?: string;
	displayName?: string;
	// unitDescription?: string;
	sequence?: number;
	status?: Status;
	lessons?: CurriculumLessonInput[];
};

export type CurriculumTrackInput = {
	id: string;
	// trackName?: string;
	// displayName?: string;
	// trackDescription?: string;
	// sequence?: number;
	// status?: Status;
	units?: CurriculumUnitInput[];
};

type CurriculumLessonPlanInput = {
	id: string;
	curriculumId: string;
	locale?: Locale;
	lessonPlanName?: string;
	displayName?: string;
	description?: string;
	// sequence?: number;
	// iconUrl?: string;
	// isFinalized?: boolean;
	// status?: Status;
	tracks?: CurriculumTrackInput[];
};

type CurriculumLessonPlansFilter = {
	entityTypes?: string;
  statuses?: Status[];
};

type CurriculumLessonPlansParams = {
	districtId: string;
	grade: Grade;
	schoolYear: number;
	filter?: CurriculumLessonPlansFilter;
};

export type SaveCurriculumLessonPlanParams = {
	districtId: string;
	grade: Grade;
	schoolYear: number;
	savedBy: string;
	plans: CurriculumLessonPlanInput[];
	overrideGradeData?: boolean;
};

export type CurriculumLessonPlan = {
  id: string;
  sequence: number;
  lessonPlanName: string;
  curriculumId: string;
  displayName: string;
  description: string;
  grade: Grade;
  iconUrl?: string;
  isFinalized?: boolean;
  tracks: CurriculumTrack[];
};

type CurriculumTrack = {
  id: string;
  sequence: number;
  displayName: string;
  units: CurriculumUnit[];
};

export type CurriculumUnit = {
  id: string;
  sequence: number;
  displayName: string;
  lessons: CurriculumLesson[];
};

export type CurriculumLesson = {
  id: string;
  sequence: number;
  displayName: string;
  dateFrom: string;
  dateTo: string;
  status: Status;
  skills: LessonSkill[];
  duration: number;
  durationType: string;
};

export type LessonSkill = {
  id: string;
  skillId: string;
  displayName: string;
  skillName: string;
  skillType: SkillType;
  sequence: number;
  windowPeriod: Window;
};

type SkillType = "FOCUS" | "OTHER";

type CurriculumLessonPlansResponse = {
	getCurriculumLessonPlans: CurriculumLessonPlan[];
};

type Exception = {
	exceptionDesc: string;
	exceptionMessage: string;
};

type StringResult = {
	data: string;
	isSuccess: boolean;
	statusCode: number;
	messages: string[];
	exceptions: Exception[];
};

type StringsResult = {
	clonePlans: {
		data: StringResult[];
		isSuccess: boolean;
		statusCode: number;
		itemCount: number;
		successCount: number;
		errorCount: number;
	};
};

type SaveCurriculumLessonPlanResult = {
	data: CurriculumLessonPlan;
	isSuccess: boolean;
	statusCode: number;
	messages: string[];
	exceptions: Exception[];
};

type SaveCurriculumLessonPlansResult = {
	data: SaveCurriculumLessonPlanResult[];
	isSuccess: boolean;
	statusCode: number;
	itemCount: number;
	successCount: number;
	errorCount: number;
};

export type AvailableCurriculumLessonPlan = {
	curriculumId: string;
	curriculumName: string;
	curriculumDescription: string;
	curriculumIconUrl: string;
	curriculumLessonPlanId: string;
	grade: Grade;
	locale: Locale;
	schoolYear: number;
};

type AvailableCurriculumLessonPlansResponseData = {
	getCurriculumLessonPlansAvailable: AvailableCurriculumLessonPlan[];
};

type FetchAvailableCurriculumLessonPlansParams = {
	districtId?: string | null;
	curriculumIds?: string[] | null;
	grades?: Grade[] | null;
	locales?: Locale[] | null;
	schoolYear?: number | null;
};

type CloneCurriculumLessonPlansParams = {
	clonedBy: string;
	districtId: string;
	schoolYear: number;
	planIds: string[];
	overridePlanData?: boolean;
};

export const saveCurriculumLessonPlan = async (
	params: SaveCurriculumLessonPlanParams,
): Promise<SaveCurriculumLessonPlansResult | undefined> => {
	const {
		districtId,
		grade,
		schoolYear,
		savedBy,
		plans,
		overrideGradeData = false,
	} = params;

	try {
		const response =
			await curriculumGraphqlRequest<SaveCurriculumLessonPlansResult>(
				GQL_SAVE_CURRICULUM_LESSON_PLAN,
				{
					districtId,
					grade,
					schoolYear,
					savedBy,
					plans,
					overrideGradeData,
				},
			);
		return response;
	} catch (error) {
		console.error("Error saving curriculum lesson plan", error);
	}
};

export const fetchCurriculumLessonPlansData = async (
	params: CurriculumLessonPlansParams,
): Promise<CurriculumLessonPlansResponse | undefined> => {
	const {
		districtId,
		grade,
		schoolYear,
		filter = {
			entityTypes: "DISTRICT",
		},
	} = params;
	try {
		const response =
			await curriculumGraphqlRequest<CurriculumLessonPlansResponse>(
				GQL_CURRICULUM_LESSON_PLANS_DATA,
				{ districtId, grade, schoolYear, filter },
			);
		return response;
	} catch (error) {
		console.error("Error fetching performance scales data", error);
	}
};

export const fetchAvailableCurriculumLessonPlans = async (
	params: FetchAvailableCurriculumLessonPlansParams,
): Promise<AvailableCurriculumLessonPlansResponseData | undefined> => {
	// Destructure optional params, defaulting to null as per GQL query defaults
	const {
		districtId = null,
		curriculumIds = null,
		grades = null,
		locales = null,
		schoolYear = null,
	} = params;
	try {
		const response =
			await curriculumGraphqlRequest<AvailableCurriculumLessonPlansResponseData>(
				GQL_GET_CURRICULUM_LESSON_PLANS_AVAILABLE,
				// Pass the variables matching the updated GQL query
				{ districtId, curriculumIds, grades, locales, schoolYear },
			);
		return response;
	} catch (error) {
		console.error("Error fetching available curriculum lesson plans", error);
		// Return undefined in case of an error
		return undefined;
	}
};

export const cloneCurriculumLessonPlans = async (
	params: CloneCurriculumLessonPlansParams,
): Promise<StringsResult | undefined> => {
	const {
		clonedBy,
		districtId,
		schoolYear,
		planIds,
		overridePlanData = false,
	} = params;

	try {
		const response = await curriculumGraphqlRequest<StringsResult>(
			GQL_CLONE_CURRICULUM_LESSON_PLANS,
			{
				clonedBy,
				districtId,
				schoolYear,
				planIds,
				overridePlanData,
			},
		);
		return response;
	} catch (error) {
		console.error("Error cloning curriculum lesson plans", error);
		return undefined;
	}
};
